// Products — the top-level PIM.
//
// WHAT THIS REPLACED, so nobody rebuilds it: this page used to be read-only.
// "Add product" opened a modal whose only action was to navigate to
// /companies/:id/products, and every row click did the same. It could not
// create, edit, delete or organise anything — the entire page was a link.
// The product form was not missing, just trapped inside the per-company page;
// it now lives in components/ProductModal and both pages use it.
//
// Shape: merchant -> catalog -> products, which is the grouping go-pim's
// product_catalogs table models. Pick a merchant, then a catalog in the rail
// (or "All products" / "Ungrouped"), and act on products in place.
//
// Ungrouped is a REAL bucket, not an error state: catalog_id is nullable on
// purpose and a product with no catalog is a permanent, legitimate thing.
//
// Money is bigint minor units end-to-end; display via fmtCcyMinor, never
// parseFloat.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { StatusPill, EmptyState } from "@/components/UI";
import { ProductModal } from "@/components/ProductModal";
import { CatalogModal } from "@/components/CatalogModal";
import { ConfirmDialog, type ConfirmRequest } from "@/components/ConfirmDialog";
import { fmtCcyMinor } from "@/utils/format";
import { bpsToPct } from "@/api/companies";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import { useCompanies } from "@/queries/useCompanies";
import { useCatalogs } from "@/queries/useCatalogs";
import { useProducts } from "@/queries/useProducts";
import {
  deleteCatalog,
  moveProductToCatalog,
  type Catalog,
} from "@/api/catalogs";
import { deleteProduct, type Product } from "@/api/products";

/** Rail selection: every product, the ungrouped bucket, or one catalog id. */
const ALL = "__all__";
const UNGROUPED = "__ungrouped__";

function taxLabel(scheme: string, gstRateBps: number): string {
  if (scheme === "gst") return `gst · ${bpsToPct(gstRateBps)}%`;
  return scheme;
}

export function AllProducts() {
  const qc = useQueryClient();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();

  const [companyId, setCompanyId] = useState("");
  const activeCompanyId = companyId || companies[0]?.id || "";

  const { data: catalogs = [], isLoading: catalogsLoading } =
    useCatalogs(activeCompanyId);
  const {
    data: products = [],
    isLoading: productsLoading,
    isError,
  } = useProducts(activeCompanyId);

  const [scope, setScope] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const addToast = useUI((s) => s.addToast);

  // Errors go through the SAME channel as successes. They used to be a red bar
  // that stayed until manually dismissed, so a failure from one action was
  // still on screen while the operator did the next three — and it was the only
  // feedback path that required reading a different part of the page.
  // ToastStack announces variant:"error" with aria-live=assertive, so it
  // interrupts rather than waits, which is the right urgency for a failure.
  const fail = (err: unknown, fallback: string) =>
    addToast({ title: fallback, body: apiErrorMessage(err, ""), variant: "error" });

  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<Catalog | null>(null);

  // How many products sit in each catalog, plus the ungrouped count — shown in
  // the rail so an operator can see where things are without clicking through,
  // and so "this catalog can't be deleted" is predictable before they try.
  const counts = useMemo(() => {
    const byCatalog = new Map<string, number>();
    let ungrouped = 0;
    for (const p of products) {
      if (p.catalog_id === "") ungrouped += 1;
      else byCatalog.set(p.catalog_id, (byCatalog.get(p.catalog_id) ?? 0) + 1);
    }
    return { byCatalog, ungrouped };
  }, [products]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (scope === UNGROUPED && p.catalog_id !== "") return false;
      if (scope !== ALL && scope !== UNGROUPED && p.catalog_id !== scope)
        return false;
      if (q === "") return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.hsn_sac.toLowerCase().includes(q)
      );
    });
  }, [products, scope, search]);

  const deleteProductMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: (_d, _id, ctx) => {
      setConfirm(null);
      addToast({ title: "Product deleted", body: (ctx as string) || undefined });
      return qc.invalidateQueries({ queryKey: ["products"] });
    },
    onMutate: (id: string) => products.find((p) => p.id === id)?.name,
    onError: (err) => {
      setConfirm(null);
      fail(err, "Could not delete that product");
    },
  });

  const deleteCatalogMutation = useMutation({
    mutationFn: (id: string) => deleteCatalog(id),
    onSuccess: async () => {
      setScope(ALL);
      setConfirm(null);
      addToast({ title: "Catalog deleted", body: "Its products were not affected." });
      await qc.invalidateQueries({ queryKey: ["catalogs", activeCompanyId] });
    },
    // A catalog with products is refused by the API with a message naming the
    // count. Surface it verbatim — it tells the operator exactly what blocks
    // the delete, which a generic "failed" would not.
    onError: (err) => {
      setConfirm(null);
      fail(err, "Could not delete that catalog");
    },
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, catalogId }: { id: string; catalogId: string }) =>
      moveProductToCatalog(id, catalogId),
    // Paint the move immediately. Without this the <select> reverts to its old
    // value the instant React re-renders and only corrects itself when the
    // refetch lands — which looks exactly like a rejected change.
    onMutate: async ({ id, catalogId }) => {
      const key = ["products", activeCompanyId, {}];
      await qc.cancelQueries({ queryKey: ["products"] });
      const previous = qc.getQueryData<Product[]>(key);
      if (previous) {
        qc.setQueryData<Product[]>(
          key,
          previous.map((p) => (p.id === id ? { ...p, catalog_id: catalogId } : p)),
        );
      }
      return { previous, key };
    },
    onSuccess: (_d, vars) => {
      const to =
        vars.catalogId === ""
          ? "Ungrouped"
          : (catalogs.find((c) => c.id === vars.catalogId)?.name ?? "a catalog");
      addToast({ title: "Product moved", body: `Now in ${to}.` });
      return qc.invalidateQueries({ queryKey: ["products"] });
    },
    // Roll the optimistic edit back on failure, or the row keeps showing a
    // catalog the product is not in.
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
      fail(err, "Could not move that product");
    },
  });

  const scopeLabel =
    scope === ALL
      ? "All products"
      : scope === UNGROUPED
        ? "Ungrouped"
        : (catalogs.find((c) => c.id === scope)?.name ?? "Catalog");

  const loading = companiesLoading || catalogsLoading || productsLoading;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · go-pim</div>
          <h1>Products</h1>
          <div className="sub">
            {loading
              ? "Loading…"
              : `${products.length} product${products.length === 1 ? "" : "s"} in ${
                  catalogs.length
                } catalog${catalogs.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="actions">
          <select
            className="input scope-picker"
            value={activeCompanyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setScope(ALL);
            }}
            aria-label="Merchant"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          <button
            className="btn"
            type="button"
            disabled={activeCompanyId === ""}
            onClick={() => {
              setEditingCatalog(null);
              setCatalogModalOpen(true);
            }}
          >
            New catalog
          </button>
          <button
            className="btn accent"
            type="button"
            disabled={activeCompanyId === ""}
            onClick={() => {
              setEditingProduct(null);
              setProductModalOpen(true);
            }}
          >
            <I.Plus size={12} /> New product
          </button>
        </div>
      </div>

      <ConfirmDialog
        request={confirm}
        busy={deleteProductMutation.isPending || deleteCatalogMutation.isPending}
        onClose={() => setConfirm(null)}
      />

      {activeCompanyId !== "" && (
        <>
          <ProductModal
            companyId={activeCompanyId}
            product={editingProduct}
            catalogs={catalogs}
            defaultCatalogId={
              scope === ALL || scope === UNGROUPED ? "" : scope
            }
            open={productModalOpen}
            onClose={() => setProductModalOpen(false)}
          />
          <CatalogModal
            companyId={activeCompanyId}
            catalog={editingCatalog}
            open={catalogModalOpen}
            onClose={() => setCatalogModalOpen(false)}
          />
        </>
      )}

      <div className="row gap-16" style={{ alignItems: "flex-start" }}>
        {/* ── Catalog rail ── */}
        <div className="card" style={{ width: 260, flexShrink: 0, padding: 8 }}>
          <RailItem
            label="All products"
            count={products.length}
            selected={scope === ALL}
            onClick={() => setScope(ALL)}
          />
          <RailItem
            label="Ungrouped"
            count={counts.ungrouped}
            selected={scope === UNGROUPED}
            onClick={() => setScope(UNGROUPED)}
          />
          <div className="rail-title">Catalogs</div>
          {catalogs.length === 0 ? (
            <div className="dim" style={{ fontSize: 12, padding: "4px 10px 10px" }}>
              None yet. Catalogs group this merchant's products.
            </div>
          ) : (
            catalogs.map((c) => (
              <RailItem
                key={c.id}
                label={c.name}
                count={counts.byCatalog.get(c.id) ?? 0}
                selected={scope === c.id}
                muted={!c.active}
                onClick={() => setScope(c.id)}
                onEdit={() => {
                  setEditingCatalog(c);
                  setCatalogModalOpen(true);
                }}
                onDelete={() => {
                  const n = counts.byCatalog.get(c.id) ?? 0;
                  if (n > 0) {
                    // Not an error and not a transient success — an explanation
                    // of why the thing they just clicked cannot happen. It
                    // persists until acknowledged and sits where they were
                    // looking, which a toast would not.
                    setConfirm({
                      title: `"${c.name}" is not empty`,
                      body: `It still has ${n} product${n === 1 ? "" : "s"}. Move ${n === 1 ? "it" : "them"} to another catalog or to Ungrouped first — deleting a catalog never deletes the products inside it.`,
                      confirmLabel: "Show its products",
                      onConfirm: () => {
                        setScope(c.id);
                        setConfirm(null);
                      },
                    });
                    return;
                  }
                  setConfirm({
                    title: `Delete "${c.name}"?`,
                    // The consequence is the whole point of asking: an operator
                    // deleting a grouping needs to know the priced items inside
                    // it are safe.
                    body: "The catalog is removed. Products are never deleted or ungrouped by this — this catalog is already empty.",
                    confirmLabel: "Delete catalog",
                    destructive: true,
                    onConfirm: () => deleteCatalogMutation.mutate(c.id),
                  });
                }}
              />
            ))
          )}
        </div>

        {/* ── Products ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="toolbar">
            <div className="input-group" style={{ flex: 1, maxWidth: 360 }}>
              <span className="addon left">
                <I.Search size={13} />
              </span>
              <input
                className="input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, category or HSN"
                aria-label="Search products"
              />
            </div>
            <div className="dim" style={{ fontSize: 12 }}>
              {scopeLabel} · {visible.length}
            </div>
          </div>

          <div className="card">
            {loading ? (
              <div className="empty">
                <div className="dim" style={{ fontSize: 13 }}>
                  Loading…
                </div>
              </div>
            ) : isError ? (
              <div className="empty">
                <div className="icon">
                  <I.Wallet size={16} />
                </div>
                <h3>Couldn't load products</h3>
                <p>The request to the go-pim admin API failed.</p>
              </div>
            ) : companies.length === 0 ? (
              <EmptyState
                icon={<I.Building size={16} />}
                title="No merchants yet"
                body="Products belong to a merchant, so there is nowhere to put one yet."
                action={
                  <Link className="btn accent" to="/">
                    Add a merchant
                  </Link>
                }
              />
            ) : visible.length === 0 && search.trim() !== "" ? (
              <EmptyState
                icon={<I.Search size={16} />}
                title="No matches"
                body={`Nothing in ${scopeLabel} matches "${search.trim()}".`}
                action={
                  <button
                    className="btn"
                    type="button"
                    onClick={() => setSearch("")}
                  >
                    Clear search
                  </button>
                }
              />
            ) : visible.length === 0 ? (
              // Every empty state offers the action that resolves it. The old
              // copy said "Use New product to add one here" and pointed at a
              // button in a different corner of the page.
              <EmptyState
                icon={<I.Wallet size={16} />}
                title={`Nothing in ${scopeLabel}`}
                body={
                  scope === UNGROUPED
                    ? "Every product of this merchant is filed in a catalog."
                    : catalogs.length === 0
                      ? "This merchant has no catalogs yet. You can add products without one — Ungrouped is a normal place for them to live."
                      : "Add the first product here."
                }
                action={
                  <button
                    className="btn accent"
                    type="button"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductModalOpen(true);
                    }}
                  >
                    <I.Plus size={12} /> New product
                  </button>
                }
              />
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Catalog</th>
                    <th>Unit price</th>
                    <th>Tax</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="cell-name">{p.name}</div>
                        <div className="cell-id dim">
                          {p.category || "—"}
                          {p.hsn_sac ? ` · HSN ${p.hsn_sac}` : ""}
                        </div>
                      </td>
                      <td>
                        {/* Inline move. Uses the dedicated move endpoint, not
                            the product update, which ignores catalog_id. */}
                        <select
                          className="input"
                          style={{ maxWidth: 170 }}
                          value={p.catalog_id}
                          disabled={moveMutation.isPending}
                          aria-label={`Catalog for ${p.name}`}
                          onChange={(e) => {
                            moveMutation.mutate({
                              id: p.id,
                              catalogId: e.target.value,
                            });
                          }}
                        >
                          <option value="">Ungrouped</option>
                          {catalogs.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="mono">
                        {fmtCcyMinor(p.unit_rate_minor, p.currency)}
                        {p.unit ? (
                          <span className="dim"> / {p.unit}</span>
                        ) : null}
                      </td>
                      <td className="dim">
                        {taxLabel(p.tax_scheme, p.gst_rate_bps)}
                      </td>
                      <td>
                        <StatusPill status={p.active ? "active" : "retired"} />
                      </td>
                      <td className="right">
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="btn ghost sm"
                            type="button"
                            onClick={() => {
                              setEditingProduct(p);
                              setProductModalOpen(true);
                            }}
                          >
                            <I.Edit size={11} /> Edit
                          </button>
                          <button
                            className="btn ghost sm"
                            type="button"
                            disabled={deleteProductMutation.isPending}
                            onClick={() => {
                              setConfirm({
                                title: `Delete "${p.name}"?`,
                                body: "This cannot be undone. Documents already issued keep their frozen copy of this product, so past receipts and invoices are unaffected.",
                                confirmLabel: "Delete product",
                                destructive: true,
                                onConfirm: () =>
                                  deleteProductMutation.mutate(p.id),
                              });
                            }}
                          >
                            <I.Trash size={11} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RailItem({
  label,
  count,
  selected,
  muted,
  onClick,
  onEdit,
  onDelete,
}: {
  label: string;
  count: number;
  selected: boolean;
  muted?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const cls = [
    "rail-item",
    selected ? "is-selected" : "",
    muted ? "is-muted" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      className={cls}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
    >
      <span className="rail-label">{label}</span>
      <span className="rail-count mono">{count}</span>
      {(onEdit || onDelete) && (
        <span className="rail-actions">
          {onEdit && (
            <button
              className="icon-btn"
              type="button"
              aria-label={`Edit ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <I.Edit size={11} />
            </button>
          )}
          {onDelete && (
            <button
              className="icon-btn"
              type="button"
              aria-label={`Delete ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <I.Trash size={11} />
            </button>
          )}
        </span>
      )}
    </div>
  );
}
